import { supabase, getValidAccessToken } from '../../lib/supabase';
import { throwFunctionsInvokeError } from '../../lib/functionsInvoke';
import { acceptChatSendResponse, recoverChatSendReceipt } from './chatSendReceipt';
import {
    getInstagramOAuthRedirectUri,
    InstagramOAuthRedirectPending,
    readInstagramOAuthCallback,
    startInstagramOAuthRedirect,
    clearInstagramOAuthUrlParams,
    consumeInstagramOAuthCallback,
} from './instagramSDK';

type EdgeFunctionErrorBody = { error?: string };

export interface InstagramModalData {
    accessToken: string;
    expiresIn?: number;
    organizationId: string;
    accounts: InstagramAccountFromApi[];
}

export interface InstagramAccountFromApi {
    pageId: string;
    pageName: string;
    instagramBusinessAccountId: string;
    instagramUsername: string;
    profilePictureUrl?: string;
}

export {
    InstagramOAuthRedirectPending,
    startInstagramOAuthRedirect,
    clearInstagramOAuthUrlParams,
    getInstagramOAuthRedirectUri,
};

let exchangeInFlight: Promise<InstagramModalData> | null = null;

async function invokeInstagramApi<T extends Record<string, unknown>>(
    body: Record<string, unknown>,
    fallback: string,
    onAccepted?: () => void,
): Promise<T> {
    await getValidAccessToken();
    const { data, error } = await supabase.functions.invoke('instagram-api', { body });
    const httpStatus = (error as { context?: { status?: number } } | null)?.context?.status;
    if (error && typeof body.requestId === 'string' && (httpStatus == null || httpStatus >= 500)) {
        return await recoverChatSendReceipt(body.requestId, onAccepted) as T;
    }
    if (error || (data as EdgeFunctionErrorBody | null)?.error) {
        await throwFunctionsInvokeError(data, error, fallback);
    }
    return typeof body.requestId === 'string' ? acceptChatSendResponse(data as T, body.requestId, onAccepted) : data as T;
}

async function finishInstagramConnect(
    organizationId: string,
    callback: NonNullable<ReturnType<typeof readInstagramOAuthCallback>>,
): Promise<InstagramModalData> {
    if (callback.organizationId !== organizationId) {
        throw new Error('Organização do callback OAuth não confere.');
    }

    const tokenData = await invokeInstagramApi<{ access_token: string; expires_in?: number; user_id?: string }>({
        action: 'exchange-token',
        organizationId,
        code: callback.code,
        redirectUri: callback.redirectUri,
    }, 'Erro ao validar login com Instagram');

    if (!tokenData?.access_token) {
        throw new Error('Falha ao trocar token');
    }

    const assetsData = await invokeInstagramApi<{ accounts: InstagramAccountFromApi[] }>({
        action: 'fetch-accounts',
        organizationId,
        accessToken: tokenData.access_token,
        userId: tokenData.user_id,
    }, 'Erro ao listar contas Instagram');

    const accounts: InstagramAccountFromApi[] = assetsData?.accounts || [];
    if (!accounts.length) {
        throw new Error('Nenhuma conta Instagram profissional encontrada.');
    }

    return {
        accessToken: tokenData.access_token,
        expiresIn: tokenData.expires_in,
        organizationId,
        accounts,
    };
}

export const connectInstagramAccount = async (
    organizationId: string
): Promise<InstagramModalData> => {
    const callback = readInstagramOAuthCallback();
    if (!callback) {
        startInstagramOAuthRedirect(organizationId);
        throw new InstagramOAuthRedirectPending();
    }

    if (exchangeInFlight) return exchangeInFlight;

    consumeInstagramOAuthCallback(callback);

    exchangeInFlight = finishInstagramConnect(organizationId, callback).finally(() => {
        exchangeInFlight = null;
    });
    return exchangeInFlight;
};

export const sendInstagramMessage = async (params: {
    requestId?: string;
    onAccepted?: () => void;
    organizationId: string;
    integrationId: string;
    recipientIgsid: string;
    text?: string;
    attachmentType?: 'audio' | 'image' | 'video' | 'document';
    mediaUrl?: string;
    mediaBase64?: string;
    mimeType?: string;
    fileName?: string;
}): Promise<{ messageId?: string; mediaUrl?: string }> => {
    const data = await invokeInstagramApi<{ messageId?: string; mediaUrl?: string }>({
        action: 'send-message',
        requestId: params.requestId || crypto.randomUUID(),
        organizationId: params.organizationId,
        integrationId: params.integrationId,
        recipientIgsid: params.recipientIgsid,
        ...(params.text ? { text: params.text } : {}),
        ...(params.attachmentType && params.mediaUrl
            ? { attachmentType: params.attachmentType, mediaUrl: params.mediaUrl }
            : {}),
        ...(params.attachmentType && params.mediaBase64
            ? {
                attachmentType: params.attachmentType,
                mediaBase64: params.mediaBase64,
                mimeType: params.mimeType,
                fileName: params.fileName,
            }
            : {}),
    }, 'Erro ao enviar mensagem Instagram', params.onAccepted);
    return { messageId: data?.messageId, mediaUrl: data?.mediaUrl };
};
